import { useCallback, useEffect, useRef, useState } from "react";
import { Send, Square } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { MAX_ASSISTANT_INPUT_LENGTH } from "./constants";
import { AssistantMessage } from "./AssistantMessage";
import { useAssistantChat } from "./useAssistantChat";

/** How close to the bottom counts as "following along" for auto-scroll. */
const STICK_TO_BOTTOM_PX = 80;

export interface AssistantConversationProps {
  projectId?: string;
  campaignId?: string;
  /** Extra classes for the scrolling message area — lets each surface set its own gutter. */
  messagesClassName?: string;
  /** Extra classes for the input row. */
  inputClassName?: string;
  /**
   * Hook for the surface's own chrome to reach `startNewConversation`, which
   * lives inside this component because it belongs to the chat hook.
   */
  onReady?: (controls: { startNewConversation: () => void }) => void;
}

/**
 * The conversation itself — messages, empty state, streaming indicator, input.
 *
 * Extracted so the slide-over and the fullscreen page render the exact same
 * chat rather than two drifting copies. Each surface supplies its own chrome.
 *
 * IMPORTANT: this calls `useAssistantChat`, which owns a conversation id, runs
 * a hydration fetch, and arms an automatic approval resume. Two of these
 * mounted on the same scope at once would double-fetch and could double-fire
 * that resume — so exactly one must be on screen at a time. `MarketplaceShell`
 * enforces that by not rendering the launcher on the fullscreen route.
 */
export function AssistantConversation({
  projectId,
  campaignId,
  messagesClassName = "px-5 py-5",
  inputClassName = "px-5 py-4",
  onReady,
}: AssistantConversationProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages,
    sendMessage,
    status,
    stop,
    error,
    clearError,
    addToolApprovalResponse,
    isHydrating,
    hasPendingApproval,
    startNewConversation,
  } = useAssistantChat({ projectId, campaignId });

  const isStreaming = status === "submitted" || status === "streaming";
  // Hydration counts as busy: sending before the stored thread arrives would
  // post a one-message history, and the server rewrites the thread from what it
  // receives — destroying the real conversation.
  const isBusy = isStreaming || isHydrating;
  const isBlocked = isBusy || hasPendingApproval;

  // Held in a ref so the effect below does not re-run whenever the parent
  // re-renders with a fresh callback identity.
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    onReadyRef.current?.({ startNewConversation });
  }, [startNewConversation]);

  // Follow the stream only while the user is already at the bottom, so scrolling
  // up to re-read something doesn't get yanked back down on the next token.
  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom > STICK_TO_BOTTOM_PX) return;

    messagesEndRef.current?.scrollIntoView({
      behavior: isStreaming ? "auto" : "smooth",
    });
  }, [messages, isStreaming]);

  const submit = useCallback(() => {
    const text = input.trim();
    if (!text || isBlocked || text.length > MAX_ASSISTANT_INPUT_LENGTH) return;

    clearError();
    setInput("");
    void sendMessage({ text });
  }, [input, isBlocked, clearError, sendMessage]);

  const overLimit = input.length > MAX_ASSISTANT_INPUT_LENGTH;

  return (
    <>
      <div ref={scrollRef} className={`flex-1 overflow-y-auto space-y-4 ${messagesClassName}`}>
        {isHydrating && messages.length === 0 && (
          <div className="space-y-2" aria-hidden="true">
            <div className="h-3 w-2/3 rounded bg-neutral-100 animate-pulse" />
            <div className="h-3 w-1/2 rounded bg-neutral-100 animate-pulse" />
          </div>
        )}

        {!isHydrating && messages.length === 0 && (
          <div className="text-sm text-neutral-500 space-y-2">
            <p>{t("assistant.emptyTitle")}</p>
            <ul className="list-disc list-inside text-xs text-neutral-400 space-y-1">
              <li>{t("assistant.exampleOne")}</li>
              <li>{t("assistant.exampleTwo")}</li>
              <li>{t("assistant.exampleThree")}</li>
            </ul>
          </div>
        )}

        {messages.map((message, index) => (
          <AssistantMessage
            key={message.id}
            message={message}
            isStreaming={isStreaming && index === messages.length - 1}
            // Approval responses only apply to the last message in the SDK's
            // store, so cards anywhere else must not offer live buttons.
            isLastMessage={index === messages.length - 1}
            projectId={projectId}
            onApprovalResponse={(id, approved) =>
              void addToolApprovalResponse({ id, approved })
            }
          />
        ))}

        {status === "submitted" && (
          <div className="flex justify-start">
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-neutral-400 animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            {t("assistant.error")}
          </p>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className={`border-t border-neutral-200 bg-white flex-shrink-0 ${inputClassName}`}>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
          className="flex items-end gap-2"
        >
          <textarea
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                submit();
              }
            }}
            rows={1}
            disabled={hasPendingApproval}
            placeholder={
              hasPendingApproval
                ? t("assistant.pendingApprovalPlaceholder")
                : t("assistant.placeholder")
            }
            className="flex-1 resize-none max-h-32 rounded-lg border border-neutral-200 px-3 py-2.5 text-sm text-neutral-900 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-steel-600/20 focus:border-steel-600/40 disabled:bg-neutral-50 disabled:text-neutral-400"
          />
          {isStreaming ? (
            <button
              type="button"
              onClick={stop}
              title={t("assistant.stop")}
              className="w-9 h-9 rounded-lg border border-neutral-200 text-neutral-500 flex items-center justify-center hover:bg-neutral-50 transition-colors flex-shrink-0 cursor-pointer"
            >
              <Square className="w-3.5 h-3.5" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim() || overLimit || isBlocked}
              className="w-9 h-9 rounded-lg bg-gradient-to-r from-steel-600 to-steel-700 text-white flex items-center justify-center hover:opacity-90 transition-opacity disabled:opacity-40 flex-shrink-0 cursor-pointer"
            >
              <Send className="w-4 h-4" />
            </button>
          )}
        </form>
        {hasPendingApproval && (
          <p className="mt-1.5 text-xs text-amber-600">{t("assistant.pendingApprovalHint")}</p>
        )}
        {overLimit && (
          <p className="mt-1.5 text-xs text-red-600">
            {t("assistant.tooLong", {
              count: input.length - MAX_ASSISTANT_INPUT_LENGTH,
            })}
          </p>
        )}
      </div>
    </>
  );
}
